import i18n from '@/i18n';
import { format, parseISO } from 'date-fns';
import { ptBR, enUS, es } from 'date-fns/locale';
import i18next, { t } from 'i18next';
import { useEffect, useState } from 'react';
import { capitalize } from '@mui/material';
import 'moment/locale/en-gb';
import 'moment/locale/es';
import 'moment/locale/pt-br';

export const dateFormatter = () => {
  const localeMap: { [key: string]: Locale } = {
    en: enUS,
    es: es,
    'pt-BR': ptBR,
    pt: ptBR,
  };
  const [locale, setLocale] = useState(localeMap[i18n.language] || enUS);

  useEffect(() => {
    setLocale(localeMap[i18n.language] || enUS);
  }, [i18n.language]);

  function formatDate(date: string) {
    const dateParsed = Date.parse(date);
    return format(dateParsed, 'yyyy-MM-dd');
  }
  function formatDateGeneric(date: string, formatString: string) {
    const dateParsed = Date.parse(date);
    return capitalize( format(dateParsed, formatString, {
      locale
    }));
  }

  function formatFromDateGeneric(date: Date, formatString: string) {
    return capitalize( format(date, formatString, {
      locale
    }));
  }

  function formatFromDate(date: Date) {
    return format(date, 'yyyy-MM-dd');
  }

  function formatYearMonth(date: Date) {
    return format(date, 'yyyy-MM');
  }

  function emptyStateSchedule(date: Date) {
    const formatDay = format(date, `d '${t('common:from')} `);
    const formatMonth = format(date, 'MMMM', {
      locale,
    });
    return formatDay + capitalize(formatMonth);
  }

  function confirmBannerSchedule(date: Date) {
    return capitalize(
      format(date, `MMMM d, yyyy`, {
        locale,
      })
    );
  }

  function formatToDate(dateString: string) {
    return parseISO(dateString);
  }

  function mountDate(month: Date, day: Date) {
    const yearValue = month.getFullYear();
    const monthValue = month.getMonth();
    const dayValue = day.getDate();
    return new Date(yearValue, monthValue, dayValue);
  }

  function formatGMTOffset(gmt: number) {
    const sign = gmt >= 0 ? '+' : '-';
    const abs = Math.abs(gmt).toString().padStart(2, '0');
    return `${sign}${abs}:00`;
  }

  function formatToDateGMT(date: string, time: string, gmt: number) {
    const isoDateString = `${date}T${time}:00${formatGMTOffset(gmt)}`;
    // console.log(`ISO Date String: ${isoDateString}`);

    return new Date(isoDateString);
  }

  function getLocalGMTOffset() {
    const now = new Date();
    return -now.getTimezoneOffset() / 60;
  }

  function getUserLocalTimeOrClinicTime(
    date: string,
    time: string,
    clinicGMT: number
  ) {
    const localGMT = getLocalGMTOffset();

    if (localGMT === clinicGMT) {
      return {
        date,
        time,
        display: `${date} ${time}`,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        asDate: new Date(`${date}T${time}`),
      };
    }

    const convertedDate = formatToDateGMT(date, time, clinicGMT);

    const formatter = new Intl.DateTimeFormat('pt-PT', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

    const formatted = formatter.format(convertedDate);

    return {
      date: convertedDate.toISOString().split('T')[0],
      time: formatted.split(', ')[1],
      display: formatted,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      asDate: convertedDate,
    };
  }

  function calculateTimeDifference(clinicGMT: number) {
    const localGMT = getLocalGMTOffset();
    const diff = localGMT - clinicGMT;

    const absoluteDiff = Math.abs(diff);

    if (diff > 0) {
      return `${absoluteDiff} ${t('hours_ahead')}`; 
    } else if (diff < 0) {
      return `${absoluteDiff} ${t('hours_behind')}`; 
    } else {
      return t('same_time');
    }
  }

  const formatAmPm = (time: string) => {
    const date = new Date(`2000-01-01T${time}:00`);
    const options: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
    };

    if (
      i18next.language.startsWith('pt') ||
      i18next.language.startsWith('es')
    ) {
      options.hour12 = true;
    } else {
      options.hour12 = true;
    }

    return date.toLocaleTimeString(i18next.language, options);
  };

  return {
    formatDate,
    formatToDate,
    formatYearMonth,
    formatFromDate,
    emptyStateSchedule,
    formatAmPm,
    mountDate,
    confirmBannerSchedule,
    formatDateGeneric,
    formatFromDateGeneric,
    formatGMTOffset,
    formatToDateGMT,
    getUserLocalTimeOrClinicTime,
    calculateTimeDifference,
  };
};
